// src/mixins/AnalyticsMixin.ts
import { Options, Vue } from 'vue-class-component'
import {
  normalizeActionDetails,
  resetAmplitude as resetAmplitudeTracking,
  setAmplitudeOptOut as setAmplitudeOptOutTracking,
  setAnalyticsUserId,
  setUserGroup as setAnalyticsUserGroup,
  setUserProperties as setAnalyticsUserProperties,
  trackError as trackAnalyticsError,
  trackEvent as trackAnalyticsEvent,
  trackFeatureUsage as trackAnalyticsFeatureUsage,
  trackRevenue as trackAnalyticsRevenue
} from '@/analytics'
import { AnalyticsProperties, AnalyticsRouteContext, AnalyticsValue } from '@/types/analytics'

@Options({})
export default class AnalyticsMixin extends Vue {
  // --- Flags ---

  get analyticsEnabled(): boolean {
    return process.env.VUE_APP_GA_ENABLED === 'true'
  }

  get amplitudeEnabled(): boolean {
    return process.env.VUE_APP_AMPLITUDE_ENABLED === 'true'
  }

  // --- Core tracking ---

  private getRouteContext(): AnalyticsRouteContext {
    return {
      hostname: typeof window !== 'undefined' ? window.location.hostname : undefined,
      origin: typeof window !== 'undefined' ? window.location.origin : undefined,
      page_path: this.$route.fullPath,
      page_location: typeof window !== 'undefined' ? window.location.href : this.$route.fullPath,
      route_name: this.$route.name != null ? String(this.$route.name) : undefined,
      route_category: typeof this.$route.meta?.category === 'string' ? this.$route.meta.category : undefined,
      route_feature: typeof this.$route.meta?.feature === 'string' ? this.$route.meta.feature : undefined
    }
  }

  trackEvent(eventName: string, parameters: AnalyticsProperties = {}): void {
    void trackAnalyticsEvent(
      {
        name: eventName,
        category: typeof parameters.event_category === 'string' ? parameters.event_category : 'user_interaction',
        label: typeof parameters.event_label === 'string' ? parameters.event_label : undefined,
        value: typeof parameters.value === 'number' ? parameters.value : undefined,
        customParameters: parameters
      },
      this.getRouteContext()
    )
  }

  // --- Component views ---

  trackComponentView(componentName: string, additionalData?: AnalyticsProperties): void {
    this.trackEvent('component_view', {
      event_category: 'engagement',
      event_label: componentName,
      component_name: componentName,
      ...additionalData
    })
  }

  // --- Button clicks ---

  trackButtonClick(buttonName: string, action: string, value?: AnalyticsValue | AnalyticsProperties): void {
    this.trackEvent('button_click', {
      event_category: 'interaction',
      event_label: buttonName,
      button_action: action,
      ...normalizeActionDetails(value)
    })
  }

  // --- Form interactions ---

  trackFormInteraction(formName: string, action: string, fieldName?: string): void {
    this.trackEvent('form_interaction', {
      event_category: 'form',
      event_label: formName,
      form_action: action,
      field_name: fieldName
    })
  }

  // --- Feature usage ---

  trackFeatureUsage(feature: string, action: string, details?: AnalyticsProperties): void {
    void trackAnalyticsFeatureUsage(feature, action, details, this.getRouteContext())
  }

  // --- Errors ---

  trackError(error: Error, context: string): void {
    void trackAnalyticsError(error, context, false, this.getRouteContext())
  }

  // --- Timing ---

  trackTiming(category: string, variable: string, time: number, label?: string): void {
    this.trackEvent('timing_complete', {
      event_category: category,
      event_label: label,
      name: variable,
      value: time
    })
  }

  // --- Conversions ---

  trackConversion(conversionType: string, value?: number, details?: AnalyticsProperties): void {
    this.trackEvent(conversionType, {
      event_category: 'conversion',
      value: value,
      ...details
    })
  }

  // --- User identity ---

  setUserId(userId: string): void {
    void setAnalyticsUserId(userId)
  }

  // --- User properties ---

  setUserProperties(properties: AnalyticsProperties): void {
    void setAnalyticsUserProperties(properties)
  }

  // --- Amplitude-specific: Revenue ---

  trackRevenue(productId: string, price: number, quantity: number, revenueType?: string): void {
    void trackAnalyticsRevenue(productId, price, quantity, revenueType)
  }

  // --- Amplitude-specific: User groups ---

  setUserGroup(groupType: string, groupName: string | string[]): void {
    void setAnalyticsUserGroup(groupType, groupName)
  }

  // --- Amplitude-specific: Reset on logout ---

  resetAmplitude(): void {
    void resetAmplitudeTracking()
  }

  // --- Amplitude-specific: Opt out ---

  setAmplitudeOptOut(optOut: boolean): void {
    void setAmplitudeOptOutTracking(optOut)
  }
}
